import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import { TbCopy, TbExternalLink, TbX } from "react-icons/tb";
import {
  BASE64_PREVIEW_CHARS,
  isImagePreview,
  isPdfPreview,
} from "../baasEntidadeDocumentUtils";

export const BaasEntidadeDocumentPreviewModal = ({
  open,
  title,
  src,
  contentType,
  loading,
  error,
  base64,
  base64Loading,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setCopyError(false);
    }
  }, [open, base64]);

  if (!open || typeof document === "undefined") return null;

  const pdf = isPdfPreview(contentType, src);
  const image =
    isImagePreview(contentType, src)
    || Boolean(src && !pdf && /^https?:\/\//i.test(String(src)));
  const base64Preview =
    base64 && base64.length > BASE64_PREVIEW_CHARS
      ? `${base64.slice(0, BASE64_PREVIEW_CHARS)}…`
      : base64;

  const handleCopyBase64 = async () => {
    if (!base64) return;
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(base64);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError(true);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title || intl.get("baas.entidades.detail.viewDocument")}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-brand-dark-elevated"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
            {title || intl.get("baas.entidades.detail.viewDocument")}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {src ? (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition hover:bg-gray-100 hover:text-brand-dark dark:hover:bg-brand-dark-surface dark:hover:text-gray-100"
                title={intl.get("baas.entidades.detail.openInNewTab")}
              >
                <TbExternalLink className="h-4 w-4" aria-hidden />
                {intl.get("baas.entidades.detail.openInNewTab")}
              </a>
            ) : null}
            <button
              type="button"
              className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-brand-dark dark:hover:bg-brand-dark-surface dark:hover:text-gray-100"
              aria-label={intl.get("baas.entidades.detail.closeDocumentPreview")}
              onClick={onClose}
            >
              <TbX className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
        <div className="min-h-[240px] flex-1 overflow-auto bg-gray-50 p-4 dark:bg-brand-dark">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.detail.openingDocument")}
            </p>
          ) : null}
          {!loading && error ? <p className="text-sm text-red-500">{error}</p> : null}
          {!loading && !error && src && image ? (
            <img src={src} alt={title || ""} className="mx-auto max-h-[70vh] max-w-full object-contain" />
          ) : null}
          {!loading && !error && src && pdf ? (
            <iframe title={title || "document"} src={src} className="h-[70vh] w-full rounded-md border-0 bg-white" />
          ) : null}
          {!loading && !error && src && !image && !pdf ? (
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p>{intl.get("baas.entidades.detail.previewUnsupported")}</p>
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-brand-dark underline dark:text-brand-gold"
              >
                <TbExternalLink className="h-4 w-4" aria-hidden />
                {intl.get("baas.entidades.detail.openInNewTab")}
              </a>
            </div>
          ) : null}

          {!loading && !error && (base64 || base64Loading) ? (
            <details className="group mt-4 rounded-lg border border-gray-200 bg-white open:pb-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
              <summary className="cursor-pointer list-none px-3 py-2.5 text-xs font-medium text-brand-dark marker:content-none dark:text-gray-100 [&::-webkit-details-marker]:hidden">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block text-gray-400 transition-transform group-open:rotate-90" aria-hidden>
                    ▸
                  </span>
                  {intl.get("baas.entidades.detail.base64Section")}
                  {base64Loading ? (
                    <span className="font-normal text-gray-400">
                      ({intl.get("baas.entidades.detail.base64Loading")})
                    </span>
                  ) : null}
                </span>
              </summary>
              <div className="space-y-2 border-t border-gray-100 px-3 pt-3 dark:border-brand-dark-border">
                {base64Loading && !base64 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.entidades.detail.base64Loading")}
                  </p>
                ) : null}
                {base64 ? (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {intl.get("baas.entidades.detail.base64Chars", { count: base64.length })}
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-100"
                        onClick={handleCopyBase64}
                      >
                        <TbCopy className="h-3.5 w-3.5" aria-hidden />
                        {copied
                          ? intl.get("baas.entidades.detail.base64Copied")
                          : intl.get("baas.entidades.detail.copyBase64")}
                      </button>
                    </div>
                    {copyError ? (
                      <p className="text-xs text-red-500">
                        {intl.get("baas.entidades.detail.base64CopyError")}
                      </p>
                    ) : null}
                    <pre className="max-h-28 overflow-auto whitespace-pre-wrap break-all rounded-md bg-gray-50 p-2 font-mono text-[11px] leading-relaxed text-gray-600 dark:bg-brand-dark dark:text-gray-300">
                      {base64Preview}
                    </pre>
                  </>
                ) : null}
              </div>
            </details>
          ) : null}

          {!loading && !error && src && !base64 && !base64Loading ? (
            <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
              {intl.get("baas.entidades.detail.base64Unavailable")}
            </p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BaasEntidadeDocumentPreviewModal;
